/* eslint-disable id-length */

import { type Edge, type Node, type XYPosition } from "@xyflow/react";

type EdgeWithPathOptions<E extends Edge> = E & {
  pathOptions?: { points: XYPosition[] };
};

/**
 * Shift a freshly laid out graph so the anchor node keeps its previous position.
 *
 * When the graph is re-laid out (eg. expanding or collapsing a group step) every node is
 * given new coordinates, which visually teleports the graph underneath the stationary
 * viewport. Translating the entire layout so the anchor node lands exactly where it was
 * keeps that node fixed on screen while the rest of the graph reflows around it.
 *
 * The whole layout moves in a single update alongside the new positions, unlike a
 * follow-up viewport pan which would flash the un-anchored layout for a frame.
 *
 * Only top-level nodes are shifted: child nodes are positioned relative to their parent
 * and move with it. Edge path points are in absolute coordinates so they shift by the
 * same delta. The layout is returned unchanged if the anchor is missing, is not a
 * top-level node, or hasn't moved.
 */
export default function anchorLayoutToNode<N extends Node, E extends Edge>({
  nodes,
  edges,
  anchorId,
  anchorPosition,
}: {
  nodes: N[];
  edges: EdgeWithPathOptions<E>[];
  anchorId: string;
  anchorPosition: XYPosition;
}): { nodes: N[]; edges: EdgeWithPathOptions<E>[] } {
  const anchor = nodes.find((node) => node.id === anchorId);

  // Child node positions are relative to their parent, so we can only anchor against
  // top-level nodes with absolute positions.
  if (!anchor || anchor.parentId) {
    return { nodes, edges };
  }

  const deltaX = anchorPosition.x - anchor.position.x;
  const deltaY = anchorPosition.y - anchor.position.y;

  if (deltaX === 0 && deltaY === 0) {
    return { nodes, edges };
  }

  return {
    nodes: nodes.map((node) => {
      if (node.parentId) {
        return node;
      }

      return {
        ...node,
        position: {
          x: node.position.x + deltaX,
          y: node.position.y + deltaY,
        },
      };
    }),
    edges: edges.map((edge) => {
      if (!edge.pathOptions) {
        return edge;
      }

      return {
        ...edge,
        pathOptions: {
          ...edge.pathOptions,
          points: edge.pathOptions.points.map(({ x, y }) => ({
            x: x + deltaX,
            y: y + deltaY,
          })),
        },
      };
    }),
  };
}
